import type { Position } from '@game/types';
import type { Letter } from '@game/models/Letter';
import type { Board } from '@game/models/Board';
import { getCell } from '@game/models/Board';
import { BONUS_MULTIPLIERS } from '@game/constants/gameConstants';
import { LETTER_VALUES } from '@game/constants/letterValues';

interface WordScore {
  baseScore: number;
  letterBonuses: number;
  wordMultiplier: number;
  totalScore: number;
}

/**
 * Calculate the score for a word with bonus squares
 * 
 * Word score = (sum of letter values with letter bonuses) × word bonus multipliers
 * 
 * @param letters - Array of ALL letters in the word (including existing board letters)
 * @param positions - Array of ALL positions in the word
 * @param board - The game board (to check for bonus squares)
 * @param newlyPlacedPositions - Set of position keys for newly placed letters (format: "row,col")
 * @returns Detailed word score breakdown
 */
export function calculateWordScore(
  letters: Letter[],
  positions: Position[],
  board: Board,
  newlyPlacedPositions?: Set<string>
): WordScore {
  let baseScore = 0;
  let letterBonuses = 0;
  let wordMultiplier = 1;
  
  console.log('💰 Calculating score:');
  
  // Calculate letter scores with bonuses
  for (let i = 0; i < letters.length; i++) {
    const letter = letters[i];
    const position = positions[i];
    const cell = getCell(board, position);
    
    if (!cell) continue;
    
    const letterValue = LETTER_VALUES[letter.character];
    let finalLetterValue = letterValue;
    
    // Check if this is a newly placed letter
    const positionKey = `${position.row},${position.col}`;
    const isNewlyPlaced = newlyPlacedPositions ? newlyPlacedPositions.has(positionKey) : !cell.isOccupied;
    
    // Apply bonus only if this is a newly placed letter and cell has a bonus
    if (isNewlyPlaced && cell.bonusType) {
      const bonus = cell.bonusType;
      
      if (bonus === 'DL') {
        finalLetterValue = letterValue * BONUS_MULTIPLIERS.DL;
        letterBonuses += letterValue * (BONUS_MULTIPLIERS.DL - 1);
        console.log(`  '${letter.character}' at ${positionKey}: ${letterValue} × ${BONUS_MULTIPLIERS.DL} (DL) = ${finalLetterValue}`);
      } else if (bonus === 'TL') {
        finalLetterValue = letterValue * BONUS_MULTIPLIERS.TL;
        letterBonuses += letterValue * (BONUS_MULTIPLIERS.TL - 1);
        console.log(`  '${letter.character}' at ${positionKey}: ${letterValue} × ${BONUS_MULTIPLIERS.TL} (TL) = ${finalLetterValue}`);
      } else if (bonus === 'DW') {
        wordMultiplier *= BONUS_MULTIPLIERS.DW;
        console.log(`  '${letter.character}' at ${positionKey}: DW bonus (word × ${BONUS_MULTIPLIERS.DW})`);
      } else if (bonus === 'TW') {
        wordMultiplier *= BONUS_MULTIPLIERS.TW;
        console.log(`  '${letter.character}' at ${positionKey}: TW bonus (word × ${BONUS_MULTIPLIERS.TW})`);
      }
    } else if (isNewlyPlaced) {
      console.log(`  '${letter.character}' at ${positionKey}: ${letterValue} (new, no bonus)`);
    } else {
      console.log(`  '${letter.character}' at ${positionKey}: ${letterValue} (existing)`);
    }
    
    baseScore += finalLetterValue;
  }
  
  const totalScore = baseScore * wordMultiplier;
  
  console.log(`  Base: ${baseScore}, Word multiplier: ${wordMultiplier}x, Total: ${totalScore}`);
  
  return {
    baseScore,
    letterBonuses,
    wordMultiplier,
    totalScore,
  };
}

/**
 * Calculate simple score without bonus squares (for testing or honor system)
 */
export function calculateSimpleWordScore(letters: Letter[]): number {
  return letters.reduce((sum, letter) => sum + LETTER_VALUES[letter.character], 0);
}
